import { uuid } from "@xapp/chat-widget-core";
import { ChatUserInfo, WidgetConfigurableMessagesConfig, WidgetHooks } from "@xapp/stentor-chat-widget";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { setAccountStatus, setConnectionStatus } from "../store/actions";
import { ACCOUNT_STATUS_TYPE, ActionType, CONNECTION_STATUS_TYPE, ToolCallInfo } from "../store/ChatAction";
import { err, log } from "../utils";
import { getConfigurableMessagesConfig, ConfigurableMessagesItems } from "../utils/configurableMessages";
import { Display } from "stentor-models";
import { ChatServer, ChatServerOptions, OfflineMessage, VisitorInfoMessage } from "./ChatServer";
import { ChatMessageRequest, ChatServerMessage } from "./ChatServerMessage";
import setSessionId from "../store/actions/setSessionId";

// Endpoint path constants
const HOMEOWNER_ENDPOINT_PATH = "/chat/stream";
const HOMEOWNER_CLOSE_ENDPOINT_PATH = "/chat/close";
const BUSINESS_OWNER_ENDPOINT_PATH = "/api/chat/stream";
const BUSINESS_OWNER_CLOSE_ENDPOINT_PATH = "/api/chat/close";

// Default retry configuration for MCP connections
const DEFAULT_MCP_RETRY_CONFIG: ConfigurableMessagesItems[] = [
    { retry: 0, delay: 0, text: "" }, // Initial attempt (silent)
    { retry: 1, delay: 3, text: "Having trouble connecting. Retrying in ${sec} seconds..." },
    { retry: 2, delay: 5, text: "Still having trouble. Retrying in ${sec} seconds..." },
    { retry: 0, delay: 0, text: "Unable to connect to the assistant. Please try again later." }, // Final failure
];

// Streaming throttle configuration
// Average reading speed is ~250 words/min (~20 chars/sec). We go slightly faster.
const STREAM_CHARS_PER_TICK = 3; // Characters to display per tick
const STREAM_TICK_INTERVAL_MS = 30; // Milliseconds between ticks (~100 chars/sec)

/**
 * Page context to provide information about the user's current page.
 * Enables more relevant AI responses based on context.
 */
interface PageContext {
    url?: string;       // Full URL: "https://example.com/pricing?ref=nav"
    path?: string;      // Path only: "/pricing"
    title?: string;     // document.title: "Pricing - XAPP AI"
    referrer?: string;  // document.referrer: "https://example.com/home"
    localTime?: string; // ISO timestamp with local timezone offset: "2024-01-15T14:30:00-04:00"
}

/**
 * Device/browser fingerprint for user identification.
 *
 * PRIVACY NOTE: This data is used solely to identify returning users
 * for session continuity. It is not used for tracking across sites
 * or shared with third parties. The data collected is limited to
 * basic browser/device characteristics that do not personally identify users.
 */
interface ClientFingerprint {
    userAgent?: string;
    language?: string;
    timezone?: string;
    screenResolution?: string;
    platform?: string;
    colorDepth?: number;
    touchSupport?: boolean;
}

/**
 * Storage keys for MCP session persistence.
 * Uses localStorage directly (not Redux) for MCP-specific data.
 */
const MCP_STORAGE_KEYS = {
    USER_ID: "xapp_mcp_userId",
    SESSION_ID: "xapp_mcp_sessionId",
} as const;

/**
 * Gets the platform string, preferring the modern userAgentData API.
 * Falls back to the deprecated navigator.platform for older browsers.
 */
function getPlatform(): string | undefined {
    // Modern API (Chrome 90+, Edge 90+, Opera 76+)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const userAgentData = (navigator as any).userAgentData;
    if (userAgentData?.platform) {
        return userAgentData.platform;
    }
    // Fallback for older browsers (deprecated but widely supported)
    return navigator.platform || undefined;
}

/**
 * Collects non-invasive device/browser fingerprint data.
 * This helps the server identify returning users without requiring login.
 *
 * PRIVACY NOTE: Only basic, non-identifying browser characteristics are collected.
 * This data cannot be used to personally identify individual users.
 */
function getFingerprint(): ClientFingerprint {
    if (typeof window === "undefined") {
        return {};
    }

    return {
        userAgent: navigator.userAgent,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        platform: getPlatform(),
        colorDepth: window.screen.colorDepth,
        touchSupport: "ontouchstart" in window || navigator.maxTouchPoints > 0,
    };
}

/**
 * Retrieves stored MCP identity from localStorage.
 */
function getStoredIdentity(): { userId?: string; sessionId?: string } {
    if (typeof window === "undefined" || !window.localStorage) {
        return {};
    }

    try {
        return {
            userId: localStorage.getItem(MCP_STORAGE_KEYS.USER_ID) || undefined,
            sessionId: localStorage.getItem(MCP_STORAGE_KEYS.SESSION_ID) || undefined,
        };
    } catch {
        // localStorage may be unavailable (private browsing, etc.)
        return {};
    }
}

/**
 * Stores MCP identity to localStorage.
 */
function storeIdentity(userId?: string, sessionId?: string): void {
    if (typeof window === "undefined" || !window.localStorage) {
        return;
    }

    try {
        if (userId) {
            localStorage.setItem(MCP_STORAGE_KEYS.USER_ID, userId);
        }
        if (sessionId) {
            localStorage.setItem(MCP_STORAGE_KEYS.SESSION_ID, sessionId);
        }
    } catch {
        // localStorage may be unavailable
    }
}

/**
 * Clears stored MCP session (but preserves userId for returning user identification).
 * Optionally clears the stored lastSeq for the given sessionId.
 */
function clearStoredSession(sessionId?: string): void {
    if (typeof window === "undefined" || !window.localStorage) {
        return;
    }

    try {
        localStorage.removeItem(MCP_STORAGE_KEYS.SESSION_ID);
        if (sessionId) {
            clearStoredLastSeq(sessionId);
        }
    } catch {
        // localStorage may be unavailable
    }
}

/**
 * Retrieves stored lastSeq from sessionStorage for replay on reconnect.
 * Uses sessionStorage (not localStorage) because seq is ephemeral to the browser tab.
 */
function getStoredLastSeq(sessionId: string): number | undefined {
    if (typeof window === "undefined" || !window.sessionStorage) {
        return undefined;
    }

    try {
        const stored = sessionStorage.getItem(`xapp_mcp_lastSeq_${sessionId}`);
        return stored ? parseInt(stored, 10) : undefined;
    } catch {
        return undefined;
    }
}

/**
 * Persists lastSeq to sessionStorage for replay on reconnect.
 */
function storeLastSeq(sessionId: string, seq: number): void {
    if (typeof window === "undefined" || !window.sessionStorage) {
        return;
    }

    try {
        sessionStorage.setItem(`xapp_mcp_lastSeq_${sessionId}`, String(seq));
    } catch {
        // sessionStorage may be unavailable
    }
}

/**
 * Clears stored lastSeq from sessionStorage.
 */
function clearStoredLastSeq(sessionId: string): void {
    if (typeof window === "undefined" || !window.sessionStorage) {
        return;
    }

    try {
        sessionStorage.removeItem(`xapp_mcp_lastSeq_${sessionId}`);
    } catch {
        // sessionStorage may be unavailable
    }
}

/**
 * Returns the current local time as an ISO 8601 string with the local timezone offset.
 * Unlike toISOString(), this preserves the user's local time zone (e.g. "2024-01-15T14:30:00-04:00")
 * so the agent server can correctly determine time-of-day context (morning greetings, etc.).
 */
export function getLocalISOString(date: Date = new Date()): string {
    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const pad = (n: number) => String(Math.abs(n)).padStart(2, "0");
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const offsetMins = Math.abs(offsetMinutes) % 60;
    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
        `${sign}${pad(offsetHours)}:${pad(offsetMins)}`
    );
}

/**
 * Gets the current page context from browser globals.
 * If an override is provided, uses that instead (useful for testing).
 *
 * PRIVACY NOTE: Query strings are stripped from URLs by default as they
 * may contain sensitive data (tokens, PII). Only origin + pathname are sent.
 */
function getPageContext(override?: PageContext): PageContext {
    const localTime = getLocalISOString();

    // If override provided, merge with defaults (override wins)
    // Overrides are typically for testing and may include full URLs
    if (override && Object.keys(override).length > 0) {
        log(`[getPageContext] Using override: ${JSON.stringify(override)}`);
        return {
            url: override.url,
            path: override.path,
            title: override.title,
            referrer: override.referrer,
            localTime,
        };
    }

    if (typeof window === "undefined") {
        return { localTime };
    }

    // Strip query strings from URL for privacy (may contain tokens/PII)
    const urlWithoutQuery = window.location.origin + window.location.pathname;
    // Strip query strings from referrer as well
    let referrerWithoutQuery: string | undefined;
    if (document.referrer) {
        try {
            const refUrl = new URL(document.referrer);
            referrerWithoutQuery = refUrl.origin + refUrl.pathname;
        } catch {
            // Invalid referrer URL, skip it
            referrerWithoutQuery = undefined;
        }
    }

    return {
        url: urlWithoutQuery,
        path: window.location.pathname,
        title: document.title || undefined,
        referrer: referrerWithoutQuery,
        localTime,
    };
}

export interface MCPChatConfig {
    /**
     * Server URL for MCP connections.
     *
     * For homeowner mode: Include the appId in the URL
     * e.g., "https://agent.xapp.ai/abc123"
     *
     * For business_owner mode: Just the base URL
     * e.g., "https://agent.xapp.ai"
     */
    readonly serverUrl: string;
    /**
     * Mode determines endpoint path and authentication requirements.
     * - homeowner: appends /chat/stream to serverUrl (unauthenticated)
     * - business_owner: appends /api/chat/stream to serverUrl (requires authToken)
     *
     * @default "homeowner"
     */
    readonly mode?: "homeowner" | "business_owner";
    /**
     * Optional session ID for conversation continuity
     */
    readonly sessionId?: string;
    /**
     * Auth token required for business_owner mode.
     * Will throw an error if mode is "business_owner" and authToken is not provided.
     */
    readonly authToken?: string;
    /**
     * Enable debug mode to show tool calls in the chat
     */
    readonly debugMode?: boolean;
    /**
     * Override the automatically detected page context.
     * Useful for testing in Storybook or simulating different pages.
     */
    readonly pageContextOverride?: PageContext;
    /**
     * Show an "AI" badge on the avatar for AI-generated messages.
     */
    readonly showAiBadge?: boolean;
    /**
     * Disable device fingerprinting for privacy-conscious deployments.
     * When true, no fingerprint data is sent to the server.
     * Note: This may reduce the server's ability to identify returning users.
     */
    readonly disableFingerprinting?: boolean;
}

interface SSEEventData {
    text?: string;
    toolName?: string;
    /** Unique identifier for this tool call (handles concurrent same-name tool calls) */
    toolCallId?: string;
    /** Human-readable tool name (e.g., "Search Knowledge Base") */
    displayName?: string;
    /** Context-sensitive label for current state */
    label?: string;
    /** If true, tool call is only shown in debug mode */
    hidden?: boolean;
    toolInput?: unknown; // Input parameters for tool_start, also sent in tool_end when debug mode is active
    toolResult?: unknown;
    toolError?: string;
    /** Tool batch execution duration in ms (debug mode only, from server) */
    toolBatchDurationMs?: number;
    response?: ChatResponse;
    error?: string;
    /** Server-generated user ID (persistent across sessions) */
    userId?: string;
    /** Server-generated session ID (for current conversation) */
    sessionId?: string;
    /** Indicates if this is a returning user with existing session */
    isReturningUser?: boolean;
    reason?: string; // From session_expired event
    /** Monotonically increasing sequence number (per session) */
    seq?: number;
    /** ISO 8601 server timestamp (source of truth for ordering) */
    serverTime?: string;
    /** Echo of client timestamp if provided */
    clientTime?: string;
    /** True if this event is being replayed after reconnect */
    isReplay?: boolean;
    /** Number of events replayed (replay_complete event) */
    replayedCount?: number;
    /** Current sequence number after replay (replay_complete event) */
    currentSeq?: number;
    /** Oldest available sequence number (replay_partial event) */
    oldestAvailableSeq?: number;
}

/**
 * Parameters for dispatching tool start events.
 * Extracted to typed interface for better maintainability.
 */
interface ToolStartParams {
    messageId: string;
    toolName: string;
    toolCallId?: string;
    displayName?: string;
    label?: string;
    hidden?: boolean;
    toolInput?: unknown;
}

/**
 * Parameters for dispatching tool end events.
 * Extracted to typed interface for better maintainability.
 */
interface ToolEndParams {
    messageId: string;
    toolName: string;
    toolCallId?: string;
    displayName?: string;
    label?: string;
    hidden?: boolean;
    toolResult?: unknown;
    toolError?: string;
}

interface ChatResponse {
    /** The response text from the server */
    response?: string;
    sessionId?: string;
    suggestions?: ChatSuggestion[];
    sources?: ChatSourceResponse[];
    displays?: readonly Display[];
}

interface ChatSuggestion {
    title: string;
    url?: string;
}

interface ChatSourceResponse {
    title: string;
    url: string;
    newTab?: boolean;
}

export class MCPChat implements ChatServer {
    private visitorInfo!: VisitorInfoMessage;
    private _userId = "";
    private _sessionId = "";
    private _accessToken = "";
    private _debugMode = false;
    private _attributes: Record<string, unknown> = {};
    private dispatch!: (action: ActionType) => void;
    private isNewSession: boolean;
    private readonly configurableMessages: WidgetConfigurableMessagesConfig | undefined;
    private readonly config: MCPChatConfig;
    private readonly options: ChatServerOptions | undefined;
    private readonly hooks?: WidgetHooks;
    private abortController?: AbortController;
    private currentMessageId?: string;

    // Streaming throttle state
    private streamBuffer = "";
    private streamThrottleTimer?: ReturnType<typeof setInterval>;
    private streamingMessageIdForThrottle?: string;
    private visibilityHandler?: () => void;

    // New session initialization state
    private isInitializingSession = false;
    private sessionInitPromise?: Promise<void>;
    private resolveSessionInit?: () => void;

    // Session validation state (silent handshake - no stream dispatch)
    private isValidatingSession = false;

    // Tool call tracking for debug mode
    private currentToolCalls: ToolCallInfo[] = [];

    // SSE sequencing state for replay on reconnect
    private lastSeq: number | undefined;
    private isReplaying = false;

    // Sleep/wakeup state for tab backgrounding recovery
    private isSleeping = false;
    private isRequestingReplay = false;
    private needsReplayOnWakeup = false;

    // True when userId was assigned by the server (via connected SSE event),
    // meaning it should not be overwritten by the default client fingerprint.
    private userIdIsServerAssigned = false;

    private sessionStartTime: number | undefined;
    private sentMessageCount = 0;

    constructor(config: MCPChatConfig, options?: ChatServerOptions) {
        this.config = config;
        this.options = options;
        this.configurableMessages = options?.configurableMessages;
        this.isNewSession = false;
        this._debugMode = config.debugMode ?? false;
        this.hooks = options?.hooks;

        // Validate business_owner mode requires authToken
        const mode = config.mode || "homeowner";
        if (mode === "business_owner" && !config.authToken) {
            throw new Error("authToken is required for business_owner mode");
        }

        // Load stored identity from localStorage
        const storedIdentity = getStoredIdentity();
        if (storedIdentity.userId) {
            this._userId = storedIdentity.userId;
            this.userIdIsServerAssigned = true;
            log(`[MCPChat] Loaded stored userId: ${this._userId}`);
        }
        if (storedIdentity.sessionId) {
            this._sessionId = storedIdentity.sessionId;
            log(`[MCPChat] Loaded stored sessionId: ${this._sessionId}`);
        }

        // Config-provided sessionId takes precedence
        if (config.sessionId) {
            this._sessionId = config.sessionId;
        }

        // Load lastSeq from sessionStorage for replay on reconnect
        if (this._sessionId) {
            this.lastSeq = getStoredLastSeq(this._sessionId);
            if (this.lastSeq !== undefined) {
                log(`[MCPChat] Loaded stored lastSeq: ${this.lastSeq}`);
            }
        }
    }

    public init(dispatch: (action: ActionType) => void): void {
        this.dispatch = dispatch;
        this.sessionStartTime = Date.now();
        const mode = this.config.mode || "homeowner";
        log(`MCPChat initialized with mode: ${mode}`);

        // Warn if not using HTTPS with authentication
        if (mode === "business_owner" && !this.config.serverUrl.startsWith("https://")) {
            err("WARNING: Using non-HTTPS URL with authentication tokens. This is insecure in production.");
        }

        this.setConnectionStatus("online");
        this.setAccountStatus("online");

        // Dispatch bot joined early so it appears before the greeting.
        // getBot() is config-derived (uses options.bot), so it's safe to call before setVisitorInfo.
        this.userJoined(this.getBot(undefined));

        // Session initialization is deferred to setVisitorInfo() to avoid
        // making network requests before the widget is visible.
    }

    /**
     * Validates an existing session with the server ("Silent Handshake").
     * Called when widget reopens with a stored sessionId.
     *
     * The server will respond with:
     * - "connected" event if session is valid
     * - "session_expired" event if session is gone (triggers initializeNewSession)
     *
     * This is lightweight - no LLM call, just a quick DB check to:
     * - Validate session before user tries to send a message
     * - Update lastActivityAt in persistent session
     * - Confirm userId is still valid
     */
    private async validateSession(): Promise<void> {
        const endpoint = this.getEndpoint();
        log(`Validating existing session at ${endpoint}, sessionId: ${this._sessionId}`);

        this.isValidatingSession = true;

        try {
            await this.performSSERequest(this.buildRequestPayload({ resumeSession: true }));
            log("Session validation complete");
        } catch (error) {
            err(`Failed to validate session: ${error}`);
            // If validation fails (network error, etc.), clear session and start fresh
            clearStoredSession(this._sessionId);
            this._sessionId = "";
            this.lastSeq = undefined;
            this.initializeNewSession();
        } finally {
            this.isValidatingSession = false;
        }
    }

    /**
     * Builds the common request payload with identity and context.
     */
    private buildRequestPayload(additionalData: Record<string, unknown> = {}): Record<string, unknown> {
        return {
            // Identity - server generates these, we just send back what we have
            userId: this._userId || undefined,
            sessionId: this._sessionId || undefined,
            // Fingerprint for user identification (can be disabled for privacy)
            fingerprint: this.config.disableFingerprinting ? undefined : getFingerprint(),
            // Page context
            pageContext: getPageContext(this.config.pageContextOverride),
            // Client timestamp for latency measurement and clock drift detection
            clientTime: new Date().toISOString(),
            // Last sequence number for replay on reconnect
            lastSeq: this.lastSeq,
            // Debug mode flag — server enriches SSE events with diagnostic data when enabled
            ...(this._debugMode && { debug: true }),
            // Additional data (message, newSession flag, etc.)
            ...additionalData,
        };
    }

    /**
     * Initializes a new session by calling the endpoint with { newSession: true }.
     * This returns a greeting message and session ID without making an LLM call.
     */
    private async initializeNewSession(): Promise<void> {
        // Reset debug mode on new session (respects config default)
        this._debugMode = this.config.debugMode ?? false;

        // Prevent multiple concurrent initialization requests
        if (this.isInitializingSession) {
            log("New session initialization already in progress, skipping");
            return;
        }

        const endpoint = this.getEndpoint();
        log(`Initializing new session at ${endpoint}`);

        this.isInitializingSession = true;
        // Create a promise that resolves when initialization completes
        this.sessionInitPromise = new Promise<void>((resolve) => {
            this.resolveSessionInit = resolve;
        });
        this.typing();

        try {
            await this.performSSERequest(this.buildRequestPayload({ newSession: true, generateGreeting: true }));
        } catch (error) {
            err(`Failed to initialize new session: ${error}`);
            this.stopTyping();
            this.setConnectionStatus("offline");
            // Show a failure message but don't block - user can still try sending messages
            this.sendFailureMessage(0, 0, this.getErrorMessage(error));
        } finally {
            this.isInitializingSession = false;
            // Resolve the promise so any waiting postMessage calls can proceed
            if (this.resolveSessionInit) {
                this.resolveSessionInit();
                this.resolveSessionInit = undefined;
            }
            this.sessionInitPromise = undefined;
        }
    }

    private setConnectionStatus(status: CONNECTION_STATUS_TYPE) {
        log(`SERVER: connection_update: ${JSON.stringify(status)}`);
        this.dispatch(setConnectionStatus(status));
    }

    private setAccountStatus(status: ACCOUNT_STATUS_TYPE) {
        log(`SERVER: account_status: ${JSON.stringify(status)}`);
        this.dispatch(setAccountStatus(status));
    }

    private getEndpoint(): string {
        // Normalize serverUrl to remove trailing slashes
        const serverUrl = this.config.serverUrl.replace(/\/+$/, "");
        const mode = this.config.mode || "homeowner";

        if (mode === "business_owner") {
            return `${serverUrl}${BUSINESS_OWNER_ENDPOINT_PATH}`;
        }
        // homeowner mode - serverUrl should already include appId
        return `${serverUrl}${HOMEOWNER_ENDPOINT_PATH}`;
    }

    private getCloseEndpoint(): string {
        const serverUrl = this.config.serverUrl.replace(/\/+$/, "");
        const mode = this.config.mode || "homeowner";

        if (mode === "business_owner") {
            return `${serverUrl}${BUSINESS_OWNER_CLOSE_ENDPOINT_PATH}`;
        }
        return `${serverUrl}${HOMEOWNER_CLOSE_ENDPOINT_PATH}`;
    }

    private sendNewMessage(msg: ChatServerMessage, user?: ChatUserInfo) {
        log(`SERVER: new message: ${JSON.stringify(msg)}`);

        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.msg",
                user: user || this.getBot(undefined),
                msg,
                timestamp: +new Date(),
            },
        });
    }

    private userJoined(user: ChatUserInfo) {
        log(`SERVER: user joined: ${JSON.stringify(user)}`);

        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.memberjoin",
                user,
                timestamp: +new Date(),
            },
        });
    }

    private typing() {
        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.typing",
                user: this.getBot(undefined),
                typing: true,
                timestamp: +new Date(),
            },
        });
    }

    private stopTyping() {
        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.typing",
                user: this.getBot(undefined),
                typing: false,
                timestamp: +new Date(),
            },
        });
    }

    private sendFailureMessage(retry: number, delay: number, text: string) {
        this.stopTyping();
        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.failureMsg",
                user: this.getBot(undefined),
                failureMsg: {
                    retry,
                    delay,
                    text,
                },
                timestamp: +new Date(),
            },
        });
    }

    /**
     * Gets the retry configuration, using custom config if provided or defaults.
     */
    private getRetryConfig(): ConfigurableMessagesItems[] {
        if (this.configurableMessages?.items?.length > 0) {
            return getConfigurableMessagesConfig(this.configurableMessages);
        }
        return DEFAULT_MCP_RETRY_CONFIG;
    }

    /**
     * Delays execution for the specified number of seconds.
     */
    private delay(seconds: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
    }

    /**
     * Determines a user-friendly error message based on the error type.
     */
    private getErrorMessage(error: unknown, statusCode?: number): string {
        // Auth errors
        if (statusCode === 401) {
            return "Authentication failed. Please check your credentials.";
        }
        if (statusCode === 403) {
            return "Access denied. You don't have permission to access this resource.";
        }

        // Server errors
        if (statusCode && statusCode >= 500) {
            return "The server is experiencing issues. Please try again later.";
        }

        // Not found (likely wrong appId or endpoint)
        if (statusCode === 404) {
            return "Service not found. Please check your configuration.";
        }

        // Network errors
        if (error instanceof TypeError && (error.message.includes("fetch") || error.message.includes("network"))) {
            return "Unable to connect to the server. Please check your internet connection.";
        }

        // Connection refused / server down
        if (error instanceof Error) {
            const msg = error.message.toLowerCase();
            if (msg.includes("failed to fetch") || msg.includes("network") || msg.includes("econnrefused")) {
                return "Unable to reach the server. Please try again later.";
            }
        }

        // Generic fallback
        return "Something went wrong. Please try again.";
    }

    private dispatchStreamStart(messageId: string) {
        const bot = this.getBot(undefined);
        const streamTimestamp = +new Date();
        log(`[dispatchStreamStart] Stream started: ${messageId}, timestamp: ${streamTimestamp}`);
        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.stream.start",
                user: bot,
                messageId,
                timestamp: streamTimestamp,
            },
        });
    }

    private dispatchStreamChunk(messageId: string, text: string) {
        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.stream.chunk",
                user: this.getBot(undefined),
                messageId,
                text,
                timestamp: +new Date(),
            },
        });
    }

    private dispatchStreamEnd(messageId: string, fullMessage: ChatServerMessage, toolCalls?: ToolCallInfo[]) {
        log(`Stream ended: ${messageId}`);
        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.stream.end",
                user: this.getBot(undefined),
                messageId,
                msg: fullMessage,
                toolCalls,
                timestamp: +new Date(),
            },
        });
    }

    private dispatchStreamAbort(messageId: string) {
        log(`[dispatchStreamAbort] Stream aborted: ${messageId}`);
        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.stream.abort",
                user: this.getBot(undefined),
                messageId,
                timestamp: Date.now(),
            },
        });
    }

    private dispatchStreamResume(messageId: string) {
        log(`[dispatchStreamResume] Stream resumed: ${messageId}`);
        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.stream.resume",
                user: this.getBot(undefined),
                messageId,
                timestamp: Date.now(),
            },
        });
    }

    private dispatchToolStart(params: ToolStartParams) {
        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.stream.tool_start",
                user: this.getBot(undefined),
                messageId: params.messageId,
                toolName: params.toolName,
                toolCallId: params.toolCallId,
                displayName: params.displayName,
                label: params.label,
                hidden: params.hidden,
                toolInput: params.toolInput,
                timestamp: +new Date(),
            },
        });
    }

    private dispatchToolEnd(params: ToolEndParams) {
        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.stream.tool_end",
                user: this.getBot(undefined),
                messageId: params.messageId,
                toolName: params.toolName,
                toolCallId: params.toolCallId,
                displayName: params.displayName,
                label: params.label,
                hidden: params.hidden,
                toolResult: params.toolResult,
                toolError: params.toolError,
                timestamp: +new Date(),
            },
        });
    }

    /**
     * Starts the throttled streaming for a message.
     * Text will be dispatched at a readable pace.
     */
    private startStreamThrottle(messageId: string) {
        this.streamBuffer = "";
        this.streamingMessageIdForThrottle = messageId;

        // Clear any existing timer and visibility handler
        if (this.streamThrottleTimer) {
            clearInterval(this.streamThrottleTimer);
        }
        if (this.visibilityHandler) {
            document.removeEventListener("visibilitychange", this.visibilityHandler);
        }

        // Start interval to dispatch buffered text
        this.streamThrottleTimer = setInterval(() => {
            if (this.streamBuffer.length > 0 && this.streamingMessageIdForThrottle) {
                const charsToSend = Math.min(STREAM_CHARS_PER_TICK, this.streamBuffer.length);
                const textToDispatch = this.streamBuffer.slice(0, charsToSend);
                this.streamBuffer = this.streamBuffer.slice(charsToSend);

                this.dispatchStreamChunk(this.streamingMessageIdForThrottle, textToDispatch);
            }
        }, STREAM_TICK_INTERVAL_MS);

        // Handle tab visibility changes - flush buffer when tab becomes visible
        this.visibilityHandler = () => {
            if (document.visibilityState === "visible") {
                log(`[Visibility] Tab became visible, buffer length: ${this.streamBuffer.length}, messageId: ${this.streamingMessageIdForThrottle}`);
                if (this.streamBuffer.length > 0 && this.streamingMessageIdForThrottle) {
                    // Dispatch all buffered text immediately when tab becomes visible
                    log(`[Visibility] Flushing ${this.streamBuffer.length} chars to UI`);
                    this.dispatchStreamChunk(this.streamingMessageIdForThrottle, this.streamBuffer);
                    this.streamBuffer = "";
                }
            } else {
                log(`[Visibility] Tab hidden`);
            }
        };
        document.addEventListener("visibilitychange", this.visibilityHandler);
    }

    /**
     * Adds text to the stream buffer for throttled dispatch.
     */
    private bufferStreamText(text: string) {
        this.streamBuffer += text;
    }

    /**
     * Flushes any remaining buffered text and stops the throttle timer.
     * Returns a promise that resolves when all buffered text has been dispatched.
     */
    private async flushStreamBuffer(): Promise<void> {
        // If tab is hidden or there's remaining buffer, dispatch it immediately
        if (this.streamBuffer.length > 0 && this.streamingMessageIdForThrottle) {
            // If tab is hidden, browsers throttle timers, so dispatch remaining buffer now
            if (document.visibilityState === "hidden") {
                log(`[flushStreamBuffer] Tab hidden, dispatching remaining ${this.streamBuffer.length} chars immediately`);
                this.dispatchStreamChunk(this.streamingMessageIdForThrottle, this.streamBuffer);
                this.streamBuffer = "";
            } else {
                // Wait for buffer to empty via the throttle timer
                let waitCount = 0;
                const maxWait = 100; // Max ~3 seconds
                while (this.streamBuffer.length > 0 && waitCount < maxWait) {
                    await new Promise((resolve) => setTimeout(resolve, STREAM_TICK_INTERVAL_MS));
                    waitCount++;
                }
                // If still has content after timeout, flush it
                if (this.streamBuffer.length > 0 && this.streamingMessageIdForThrottle) {
                    log(`[flushStreamBuffer] Timeout waiting for buffer, dispatching remaining ${this.streamBuffer.length} chars`);
                    this.dispatchStreamChunk(this.streamingMessageIdForThrottle, this.streamBuffer);
                    this.streamBuffer = "";
                }
            }
        }

        // Stop the throttle timer
        if (this.streamThrottleTimer) {
            clearInterval(this.streamThrottleTimer);
            this.streamThrottleTimer = undefined;
        }
        if (this.visibilityHandler) {
            document.removeEventListener("visibilitychange", this.visibilityHandler);
            this.visibilityHandler = undefined;
        }
        this.streamingMessageIdForThrottle = undefined;
    }

    /**
     * Immediately stops throttling and clears the buffer (used on abort/error).
     */
    private stopStreamThrottle() {
        if (this.streamThrottleTimer) {
            clearInterval(this.streamThrottleTimer);
            this.streamThrottleTimer = undefined;
        }
        if (this.visibilityHandler) {
            document.removeEventListener("visibilitychange", this.visibilityHandler);
            this.visibilityHandler = undefined;
        }
        this.streamBuffer = "";
        this.streamingMessageIdForThrottle = undefined;
    }

    public sendOfflineMsg(_: OfflineMessage, cb: (error?: Error) => void): void {
        cb();
    }

    public async sendChatMsg(message: ChatServerMessage, cb: (err?: Error) => void): Promise<void> {
        return this.sendChatMsgRequest(
            {
                msg: message,
                timestamp: new Date().getTime(),
                agent: false,
                user: undefined,
                type: "msg",
            },
            cb
        );
    }

    public async bargeOut(_: (err?: Error) => void): Promise<void> {
        throw new Error("bargeOut is not supported in MCP mode");
    }

    public async bargeIn(_agentName: string, __: (err?: Error) => void): Promise<void> {
        throw new Error("bargeIn is not supported in MCP mode");
    }

    /**
     * Ensures a nick has the "agent:" prefix required for agent message detection.
     */
    private ensureAgentNick(nick: string | undefined): string {
        if (!nick) return "agent:robot";
        if (nick.startsWith("agent:")) return nick;
        return `agent:${nick}`;
    }

    private getBot(user: ChatUserInfo | undefined): ChatUserInfo {
        const bot = this.options?.bot;
        if (!user) {
            // Ensure bot nick has agent: prefix and mark as AI
            const defaultBot = bot || { nick: "agent:robot", displayName: "Assistant" };
            return {
                ...defaultBot,
                nick: this.ensureAgentNick(defaultBot.nick),
                isAI: true,
            };
        }
        if (user.displayName && user.nick && user.avatarPath) {
            return {
                ...user,
                nick: this.ensureAgentNick(user.nick),
                isAI: true,
            };
        }
        return {
            ...user,
            nick: this.ensureAgentNick(user.nick || bot?.nick),
            displayName: user.displayName || bot?.displayName || "Assistant",
            avatarPath: user.avatarPath || bot?.avatarPath,
            isAI: true,
        };
    }

    public async sendChatMsgRequest(
        serviceRequest: ChatMessageRequest,
        cb: (err?: Error) => void
    ): Promise<void> {
        try {
            await this.postMessage(serviceRequest);
            cb();
        } catch (error) {
            cb(error as Error);
        }
    }

    private async postMessage(message: ChatMessageRequest): Promise<void> {
        // Don't send empty messages
        const trimmedText = message.msg?.text?.trim() || "";
        if (!trimmedText) {
            log("Ignoring empty message");
            return;
        }

        // Wait for session initialization to complete (Promise-based, no busy-wait)
        if (this.isInitializingSession && this.sessionInitPromise) {
            log("Waiting for session initialization to complete before sending message...");
            // Wait up to 10 seconds for initialization
            const timeout = new Promise<"timeout">((resolve) =>
                setTimeout(() => resolve("timeout"), 10000)
            );
            const result = await Promise.race([this.sessionInitPromise, timeout]);
            if (result === "timeout" || this.isInitializingSession) {
                err("Session initialization timed out");
                this.sendFailureMessage(0, 0, "Chat is not ready. Please try again.");
                return;
            }
        }

        const retryConfig = this.getRetryConfig();
        const messageText = message.msg?.text || "";

        let success = false;

        // Loop through retry attempts (config includes initial attempt + retries + final failure)
        for (let attempt = 0; attempt < retryConfig.length; attempt++) {
            const isLastAttempt = attempt === retryConfig.length - 1;
            const config = retryConfig[attempt];

            log(`MCP attempt ${attempt + 1}/${retryConfig.length} for message: ${messageText}`);

            // Start typing indicator
            this.typing();

            try {
                await this.attemptSSERequest(message);
                success = true;
                break; // Success - exit retry loop
            } catch (error) {
                this.stopTyping();
                const statusCode = (error as Error & { statusCode?: number })?.statusCode;
                err(`MCP attempt ${attempt + 1} failed: ${error}, statusCode: ${statusCode}`);
                log(`[postMessage] Error details:`, { error, statusCode, messageText, sessionId: this._sessionId });

                // Don't retry on 4xx client errors - these won't succeed on retry
                if (statusCode && statusCode >= 400 && statusCode < 500) {
                    log(`Client error ${statusCode}, not retrying`);
                    const errorMessage = this.getErrorMessage(error, statusCode);
                    this.sendFailureMessage(0, 0, errorMessage);
                    return;
                }

                // Set offline status
                this.setConnectionStatus("offline");

                if (isLastAttempt) {
                    // Final attempt failed - show final error message
                    const statusCode = (error as Error & { statusCode?: number })?.statusCode;
                    const errorMessage = config.text || this.getErrorMessage(error, statusCode);
                    this.sendFailureMessage(0, 0, errorMessage);
                } else if (attempt > 0 || config.text) {
                    // Show retry message (skip for silent first attempt unless it has text)
                    const nextConfig = retryConfig[attempt + 1];
                    if (nextConfig && nextConfig.delay > 0) {
                        this.sendFailureMessage(nextConfig.retry, nextConfig.delay, nextConfig.text);
                        // Wait for the delay before retrying
                        await this.delay(nextConfig.delay);
                    }
                } else {
                    // Silent first attempt - just wait briefly before retry
                    const nextConfig = retryConfig[attempt + 1];
                    if (nextConfig && nextConfig.delay > 0) {
                        await this.delay(nextConfig.delay);
                    }
                }
            }
        }

        if (success) {
            this.sentMessageCount++;
        } else {
            log("All MCP retry attempts exhausted");
        }
    }

    /**
     * Attempts a single SSE request. Throws on failure.
     */
    private async attemptSSERequest(message: ChatMessageRequest): Promise<void> {
        const messageText = message.msg?.text || "";

        // Intercept /debug command — toggle debug mode client-side without sending to LLM
        if (messageText.trim().toLowerCase() === '/debug') {
            this._debugMode = !this._debugMode;
            const statusMsg = this._debugMode ? 'Debug mode enabled' : 'Debug mode disabled';
            log(`[attemptSSERequest] ${statusMsg}`);
            // Show as a bot message
            const msgId = `debug-${Date.now()}`;
            this.dispatch({
                type: "chat",
                detail: {
                    type: "chat.stream.end",
                    user: this.getBot(undefined),
                    messageId: msgId,
                    msg: { text: statusMsg },
                    timestamp: +new Date(),
                },
            });
            return;
        }

        log(`[attemptSSERequest] Sending message: "${messageText}", userId: ${this._userId}, sessionId: ${this._sessionId}`);

        return this.performSSERequest(this.buildRequestPayload({ message: messageText }));
    }

    /**
     * Performs an SSE request with the given body.
     * Used for both new session initialization and regular messages.
     */
    private async performSSERequest(body: Record<string, unknown>): Promise<void> {
        const endpoint = this.getEndpoint();

        log(`SSE request to ${endpoint}: ${JSON.stringify(body)}`);

        // Cancel any ongoing stream
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = undefined;
        }

        this.abortController = new AbortController();
        const messageId = uuid();
        this.currentMessageId = messageId;

        const textChunks: string[] = [];
        let hasError = false;
        let httpStatusCode: number | undefined;
        let sseError: Error | undefined;

        return new Promise<void>((resolve, reject) => {
            // WARNING: Do not log headers object - it contains sensitive auth tokens
            fetchEventSource(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...((this.config.mode || "homeowner") === "business_owner" && this.config.authToken
                        ? { Authorization: `Bearer ${this.config.authToken}` }
                        : {}),
                },
                body: JSON.stringify(body),
                signal: this.abortController.signal,
                // Keep the connection open when the tab is hidden. Without this,
                // fetchEventSource closes and re-POSTs on visibility change, which
                // creates duplicate streaming messages and out-of-order responses.
                openWhenHidden: true,
                onopen: async (response) => {
                    httpStatusCode = response.status;
                    if (!response.ok) {
                        // Store status for error handling
                        const error = new Error(`HTTP error! status: ${response.status}`);
                        (error as Error & { statusCode?: number }).statusCode = response.status;
                        throw error;
                    }
                    log("SSE connection opened");
                    // Connection successful - ensure we're marked as online
                    this.setConnectionStatus("online");
                },
                onmessage: (event) => {
                    // Guard: ignore events from stale/aborted requests
                    if (this.currentMessageId !== messageId) {
                        log(`[SSE] Ignoring stale event for messageId: ${messageId}, current: ${this.currentMessageId}`);
                        return;
                    }

                    log(`SSE event: ${event.event}, data: ${event.data}`);

                    const eventType = event.event || "message";
                    let data: SSEEventData = {};

                    try {
                        data = JSON.parse(event.data);
                    } catch (e) {
                        log(`Failed to parse SSE data: ${e}`);
                        return;
                    }

                    // Track sequence number for replay on reconnect
                    if (data.seq !== undefined && data.seq !== null) {
                        this.lastSeq = data.seq;
                        if (this._sessionId) {
                            storeLastSeq(this._sessionId, data.seq);
                        }
                    }

                    switch (eventType) {
                        case "connected":
                            // Connection established
                            log(`[SSE] Connected event received, messageId: ${messageId}, isReturningUser: ${data.isReturningUser}, isReplay: ${data.isReplay}`);
                            // Track if this is a replay connection
                            if (data.isReplay) {
                                this.isReplaying = true;
                            }
                            // Store identity from server response
                            if (data.userId || data.sessionId) {
                                if (data.userId) {
                                    this._userId = data.userId;
                                    this.userIdIsServerAssigned = true;
                                }
                                if (data.sessionId) {
                                    this._sessionId = data.sessionId;
                                    this.dispatch(setSessionId(this._sessionId));
                                }
                                // Persist to localStorage for session continuity
                                storeIdentity(data.userId, data.sessionId);
                                log(`[SSE] Identity from connected - userId: ${data.userId}, sessionId: ${data.sessionId}`);
                            }
                            this.currentToolCalls = [];
                            this.stopTyping();
                            if (data.isReplay) {
                                // Replay connection — resume into the existing partial
                                // message instead of creating a duplicate placeholder
                                this.dispatchStreamResume(messageId);
                            } else {
                                // Normal connection — create new streaming placeholder
                                this.dispatchStreamStart(messageId);
                            }
                            this.startStreamThrottle(messageId);
                            break;

                        case "session_expired":
                            // Session no longer exists on server - need to start fresh
                            log(`[SSE] Session expired: ${data.reason || "Session not found"}`);
                            // Clear stored session (and lastSeq) but keep userId for returning user identification
                            clearStoredSession(this._sessionId);
                            this._sessionId = "";
                            this.lastSeq = undefined;
                            this.stopTyping();
                            this.stopStreamThrottle();
                            // Reset validation flag before starting new session
                            // (ensures connected handler will dispatch stream for greeting)
                            this.isValidatingSession = false;
                            // Start a new session automatically
                            this.initializeNewSession();
                            resolve();
                            break;

                        case "text":
                            if (data.text) {
                                log(`[SSE] Text chunk received (${data.text.length} chars), total chunks: ${textChunks.length + 1}, isReplay: ${data.isReplay}`);
                                textChunks.push(data.text);
                                if (data.isReplay || this.isSleeping) {
                                    // Replayed text or tab hidden — dispatch immediately
                                    // (no throttle timer running in either case)
                                    this.dispatchStreamChunk(messageId, data.text);
                                } else {
                                    // Live text — buffer for throttled dispatch
                                    this.bufferStreamText(data.text);
                                }
                            }
                            break;

                        case "tool_start":
                            log(`Tool execution started: ${data.toolName}, toolCallId: ${data.toolCallId}, displayName: ${data.displayName}, label: ${data.label}, hidden: ${data.hidden}`);
                            if (data.toolName) {
                                const toolCall: ToolCallInfo = {
                                    name: data.toolName,
                                    toolCallId: data.toolCallId,
                                    displayName: data.displayName,
                                    label: data.label,
                                    hidden: data.hidden,
                                    input: data.toolInput,
                                    startTime: Date.now(),
                                    isExecuting: true,
                                };
                                this.currentToolCalls.push(toolCall);
                                // Always dispatch tool start for user-friendly progress display
                                this.dispatchToolStart({
                                    messageId,
                                    toolName: data.toolName,
                                    toolCallId: data.toolCallId,
                                    displayName: data.displayName,
                                    label: data.label,
                                    hidden: data.hidden,
                                    toolInput: data.toolInput,
                                });
                            }
                            break;

                        case "tool_end":
                            log(`Tool execution ended: ${data.toolName}, toolCallId: ${data.toolCallId}, displayName: ${data.displayName}, label: ${data.label}, hidden: ${data.hidden}`);
                            if (data.toolName) {
                                // Find the matching tool call and update it
                                // Match by toolCallId if available, otherwise fall back to name + isExecuting
                                const toolCall = this.currentToolCalls.find((tc) =>
                                    data.toolCallId
                                        ? tc.toolCallId === data.toolCallId
                                        : tc.name === data.toolName && tc.isExecuting
                                );
                                if (toolCall) {
                                    toolCall.result = data.toolResult;
                                    toolCall.error = data.toolError;
                                    toolCall.label = data.label; // Update with completion label
                                    toolCall.hidden = data.hidden; // Update hidden status
                                    // Use server-side batch duration if available (debug mode), otherwise client-side
                                    toolCall.endTime = (data.toolBatchDurationMs && toolCall.startTime != null)
                                        ? toolCall.startTime + data.toolBatchDurationMs
                                        : Date.now();
                                    toolCall.isExecuting = false;
                                    // Capture tool input from debug-enriched events
                                    if (data.toolInput) {
                                        toolCall.input = data.toolInput;
                                    }
                                }
                                // Toggle debug mode when toggle_debug tool is called by the LLM
                                if (data.toolName === 'toggle_debug') {
                                    this._debugMode = !this._debugMode;
                                }
                                // Always dispatch tool end for user-friendly progress display
                                this.dispatchToolEnd({
                                    messageId,
                                    toolName: data.toolName,
                                    toolCallId: data.toolCallId,
                                    displayName: data.displayName,
                                    label: data.label,
                                    hidden: data.hidden,
                                    toolResult: data.toolResult,
                                    toolError: data.toolError,
                                });
                            }
                            break;

                        case "done":
                            this.stopTyping();
                            if (data.response) {
                                // Update session ID if provided and persist to localStorage
                                if (data.response.sessionId) {
                                    this._sessionId = data.response.sessionId;
                                    this.dispatch(setSessionId(this._sessionId));
                                    storeIdentity(undefined, data.response.sessionId);
                                }

                                // Wait for buffer to flush, then finalize
                                const accumulatedText = textChunks.join("");
                                log(`[SSE] Done event received. Total chunks: ${textChunks.length}, accumulated text length: ${accumulatedText.length}`);
                                log(`[SSE] First 100 chars of accumulated: ${accumulatedText.substring(0, 100)}`);

                                // Map suggestions to options (chips) format
                                const suggestions = data.response.suggestions;
                                const options = suggestions?.map((s) => ({
                                    label: s.title,
                                    actionUrl: s.url || "",
                                }));

                                if (options?.length) {
                                    log(`[SSE] Suggestions: ${options.length} items`);
                                }

                                // Map sources for reference links
                                const sources = data.response.sources;
                                if (sources?.length) {
                                    log(`[SSE] Sources: ${sources.length} items`);
                                }

                                const finalMessage: ChatServerMessage = {
                                    text: accumulatedText || data.response.response || "",
                                    options,
                                    sources,
                                    displays: data.response.displays,
                                };

                                // Capture tool calls before flushing (debug mode — config or runtime toggle)
                                const toolCalls = (this.config.debugMode || this._debugMode) ? [...this.currentToolCalls] : undefined;

                                this.flushStreamBuffer().then(() => {
                                    this.dispatchStreamEnd(messageId, finalMessage, toolCalls);
                                    this.currentToolCalls = []; // Clear after dispatch
                                    resolve();
                                });
                            } else {
                                this.stopStreamThrottle();
                                resolve();
                            }
                            break;

                        case "replay_complete":
                            this.isReplaying = false;
                            log(`[SSE] Replay complete: replayed ${data.replayedCount} events, currentSeq: ${data.currentSeq}`);
                            break;

                        case "replay_partial":
                            this.isReplaying = false;
                            log(`[SSE] Replay partial: some events unavailable, oldestAvailableSeq: ${data.oldestAvailableSeq}`);
                            break;

                        case "error":
                            hasError = true;
                            this.stopTyping();
                            this.stopStreamThrottle();
                            err(`SSE error: ${data.error}`);
                            // Server-side errors should not retry - reject immediately with the error
                            reject(new Error(data.error || "Server error during processing"));
                            break;
                    }
                },
                onerror: (error) => {
                    // Only handle error if this is still the current message
                    if (this.currentMessageId === messageId && !hasError) {
                        hasError = true;
                        this.isReplaying = false;

                        if (this.isSleeping) {
                            // Connection dropped while tab is hidden — don't treat as
                            // normal error. Finalize partial message and request replay
                            // when the user returns.
                            log(`[MCPChat] SSE error during sleep - will request replay on wakeup`);
                            this.needsReplayOnWakeup = true;
                            if (this.streamingMessageIdForThrottle) {
                                this.dispatchStreamAbort(messageId);
                            }
                            this.stopStreamThrottle();
                        } else {
                            this.stopStreamThrottle();
                        }

                        sseError = error instanceof Error ? error : new Error(String(error));

                        // Get status code if available
                        const statusCode = (error as Error & { statusCode?: number })?.statusCode || httpStatusCode;
                        if (statusCode) {
                            (sseError as Error & { statusCode?: number }).statusCode = statusCode;
                        }

                        err(`SSE connection error: ${error}`);
                    }
                    // Throw to stop fetchEventSource from retrying
                    throw error;
                },
            }).catch((error) => {
                // Handle errors that occur before/outside the SSE connection
                this.stopStreamThrottle();
                if (!hasError && this.currentMessageId === messageId) {
                    const statusCode = (error as Error & { statusCode?: number })?.statusCode || httpStatusCode;
                    const wrappedError = error instanceof Error ? error : new Error(String(error));
                    if (statusCode) {
                        (wrappedError as Error & { statusCode?: number }).statusCode = statusCode;
                    }
                    reject(wrappedError);
                } else if (sseError) {
                    reject(sseError);
                } else {
                    reject(error);
                }
            });
        });
    }

    public sendTyping(): void {}

    public setVisitorInfo(
        visitorInfo: VisitorInfoMessage,
        sessionId: string,
        cb: (error?: Error) => void
    ): void {
        this.visitorInfo = visitorInfo;
        this._attributes = { ...this.visitorInfo.attributes };

        // do not set currentUrl if localhost
        const href = new URL(window.location.href);
        if (href?.host.toLowerCase().startsWith(`localhost`)) {
            this._attributes.isLocal = true;
        } else {
            this._attributes.currentUrl = window.location.href;
        }

        this._accessToken = this.visitorInfo.accessToken;

        this.startSession(sessionId);

        // Initialize session now that the widget is visible and visitor info is set.
        // Returning user with existing session - validate session silently
        // New user without session - initialize new session to get greeting
        if (this._sessionId) {
            this.validateSession();
        } else {
            this.initializeNewSession();
        }

        // Show typing indicator while waiting for session init response
        if (this.isInitializingSession) {
            this.typing();
        }

        cb();
    }

    public sendChatRating(): void {}

    public sendFile(_: File, cb: (err?: Error) => void): void {
        cb(new Error("File upload not supported in MCP mode"));
    }

    public markAsRead(): void {}

    public flush(): void {}

    public dispose(): void {
        this.stopStreamThrottle();
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = undefined;
        }
    }

    public sleep(): void {
        this.isSleeping = true;

        if (this.streamingMessageIdForThrottle) {
            // Active stream in progress — keep SSE connection alive so the
            // response completes naturally while the tab is hidden.
            this.needsReplayOnWakeup = false;
            log(`[MCPChat] sleep() - stream in progress, keeping SSE connection alive`);

            // Flush any buffered text to Redux immediately
            if (this.streamBuffer.length > 0) {
                this.dispatchStreamChunk(this.streamingMessageIdForThrottle, this.streamBuffer);
                this.streamBuffer = "";
            }

            // Stop the visual throttle timer (browsers throttle timers anyway)
            // but keep streamingMessageIdForThrottle so incoming chunks can be dispatched
            if (this.streamThrottleTimer) {
                clearInterval(this.streamThrottleTimer);
                this.streamThrottleTimer = undefined;
            }
            if (this.visibilityHandler) {
                document.removeEventListener("visibilitychange", this.visibilityHandler);
                this.visibilityHandler = undefined;
            }
        } else {
            // No active stream — safe to abort the idle connection
            this.stopStreamThrottle();
            this.isReplaying = false;
            if (this.abortController) {
                this.abortController.abort();
                this.abortController = undefined;
            }
        }
    }

    public wakeup(): void {
        this.isSleeping = false;

        if (this.streamingMessageIdForThrottle && this.abortController) {
            // Stream is still in progress — restart the visual throttle
            log(`[MCPChat] wakeup() - stream still in progress, restarting throttle`);

            // Flush accumulated buffer before startStreamThrottle (which resets it)
            if (this.streamBuffer.length > 0) {
                this.dispatchStreamChunk(this.streamingMessageIdForThrottle, this.streamBuffer);
                this.streamBuffer = "";
            }
            this.startStreamThrottle(this.streamingMessageIdForThrottle);
        } else if (this.needsReplayOnWakeup) {
            this.needsReplayOnWakeup = false;
            if (this.lastSeq !== undefined && this._sessionId) {
                // Connection dropped during sleep — request replay of missed events.
                // Don't set "online" yet — requestReplay is fire-and-forget and the
                // connection status will be updated by the SSE onopen handler.
                log(`[MCPChat] wakeup() - connection lost during sleep, requesting replay`);
                this.requestReplay();
                return;
            }
            // Cannot replay — missing lastSeq or sessionId
            err(`[MCPChat] wakeup() - cannot replay, missing lastSeq (${this.lastSeq}) or sessionId (${this._sessionId})`);
        }

        if (this._sessionId) {
            log(`[MCPChat] Wakeup - session exists, lastSeq: ${this.lastSeq}`);
            this.setConnectionStatus("online");
        }
    }

    private async requestReplay(): Promise<void> {
        if (this.isRequestingReplay) {
            log("[MCPChat] Replay already in progress, skipping");
            return;
        }

        this.isRequestingReplay = true;
        this.typing();

        try {
            // lastSeq is already included by buildRequestPayload
            await this.performSSERequest(this.buildRequestPayload());
            log("[MCPChat] Replay request completed");
        } catch (error) {
            err(`[MCPChat] Replay request failed: ${error}`);
            this.setConnectionStatus("offline");
        } finally {
            this.stopTyping();
            this.isRequestingReplay = false;
        }
    }

    /**
     * Sends a best-effort close notification to the agent server.
     * Uses navigator.sendBeacon() so the request survives page unload.
     * Called from the beforeunload event handler and the widget close button.
     */
    public notifyDisconnecting(reason: "close_tab" | "widget_close" = "close_tab"): void {
        if (!this._sessionId) {
            return;
        }

        const endpoint = this.getCloseEndpoint();
        const payload = JSON.stringify({
            sessionId: this._sessionId,
            userId: this._userId || undefined,
            reason,
            messageCount: this.sentMessageCount,
            durationMs: this.sessionStartTime !== undefined ? Date.now() - this.sessionStartTime : 0,
        });

        log(`[MCPChat] notifyDisconnecting: reason=${reason}, endpoint=${endpoint}`);

        // For widget_close, the page is not unloading so fetch() can be used — it supports
        // auth headers (needed for business_owner mode) unlike sendBeacon.
        if (reason === "widget_close" && typeof fetch !== "undefined") {
            const headers: HeadersInit = { "Content-Type": "application/json" };
            if (this.config.authToken) {
                headers["Authorization"] = `Bearer ${this.config.authToken}`;
            }
            fetch(endpoint, { method: "POST", headers, body: payload, keepalive: true });
            return;
        }

        if (typeof navigator !== "undefined" && navigator.sendBeacon) {
            const blob = new Blob([payload], { type: "application/json" });
            navigator.sendBeacon(endpoint, blob);
        }
    }

    private startSession(sessionId: string): void {
        // userId priority:
        // 1. Existing server-assigned userId (from localStorage or previous connected event)
        // 2. Explicitly provided visitorId or email from host app
        // 3. Generate a new one as last resort
        if (this.userIdIsServerAssigned) {
            // Keep server-assigned userId — don't overwrite with default fingerprint
            log(`[startSession] Preserving server-assigned userId: ${this._userId}`);
        } else if (this.visitorInfo.visitorId) {
            this._userId = `${this.visitorInfo.visitorId}`;
        } else if (this.visitorInfo.email) {
            this._userId = `mcp-widget-user-${this.visitorInfo.email}`;
        } else if (!this._userId) {
            // Only generate a new userId if we don't already have one
            // (preserves userId loaded from localStorage or received from server)
            this._userId = `mcp-widget-user-${uuid()}`;
        }

        // Session ID priority:
        // 1. If explicitly provided via setVisitorInfo, use it
        // 2. If empty string provided, generate a new session ID
        // 3. If not provided (undefined) and we have an existing session, keep it
        if (sessionId) {
            // Explicit session ID provided - use it
            this._sessionId = sessionId;
            log(`Using provided session id: ${this._sessionId}`);
        } else if (sessionId === "") {
            // Empty string explicitly passed - generate new session
            this._sessionId = `mcp-widget-session-${uuid()}`;
            this.dispatch(setSessionId(this._sessionId));
            log(`Using generated session id: ${this._sessionId}`);
        } else if (!this._sessionId && this.config.sessionId) {
            // No existing session and config has one - use config
            this._sessionId = this.config.sessionId;
            log(`Using config session id: ${this._sessionId}`);
        } else if (!this._sessionId) {
            // No session ID at all - generate one
            this._sessionId = `mcp-widget-session-${uuid()}`;
            this.dispatch(setSessionId(this._sessionId));
            log(`Using generated session id: ${this._sessionId}`);
        } else {
            log(`Keeping existing session id: ${this._sessionId}`);
        }

        this.isNewSession = true;
    }

    get userId(): string {
        return this._userId;
    }

    get sessionId(): string {
        return this._sessionId;
    }
}
